/**
 * LightweightChartsProvider — Phase 52.A provider that fulfills the
 * ChartEngine contract using TradingView's `lightweight-charts` v5.
 *
 * Kept small & self-contained so a future provider (D3, Highcharts,
 * an internal WebGL engine, etc.) just needs to implement the same
 * method surface.
 */
import {
  createChart,
  CandlestickSeries,
  LineSeries,
  AreaSeries,
  HistogramSeries,
  CrosshairMode,
  LineStyle,
} from 'lightweight-charts';

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const pad2 = (n) => String(n).padStart(2, '0');

// ASCII-only formatters — sidestep `Date.toLocaleString` which throws
// RangeError when the underlying OS locale is `en-US.POSIX` (common on
// server-rendered CI runners). Explicit implementations here are also
// faster than Intl.DateTimeFormat on every tick.
function fmtTick(time) {
  const d = new Date((typeof time === 'number' ? time : Number(time)) * 1000);
  return `${MONTHS_SHORT[d.getUTCMonth()]} ${pad2(d.getUTCDate())} ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}
function fmtCrosshair(time) {
  const d = new Date((typeof time === 'number' ? time : Number(time)) * 1000);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())} ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())} UTC`;
}

const THEME = {
  layout: {
    background: { color: 'transparent' },
    textColor: '#a1a8b3',
    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
    fontSize: 11,
    // Force a valid BCP-47 locale — some server / CI environments
    // report `en-US@posix` which Intl.DateTimeFormat rejects, and
    // lightweight-charts propagates that into a RangeError on every
    // tick label. Explicit `en-US` sidesteps the issue.
    locale: 'en-US',
  },
  grid: {
    vertLines: { color: 'rgba(255,255,255,0.04)' },
    horzLines: { color: 'rgba(255,255,255,0.04)' },
  },
  rightPriceScale: {
    borderColor: 'rgba(255,255,255,0.08)',
    scaleMargins: { top: 0.08, bottom: 0.28 }, // leave 28% for volume pane
    autoScale: true,
  },
  timeScale: {
    borderColor: 'rgba(255,255,255,0.08)',
    timeVisible: true,
    secondsVisible: false,
    rightOffset: 6,
    tickMarkFormatter: fmtTick,
    // Smoother trackpad + wheel scrolling.
    shiftVisibleRangeOnNewBar: false,
  },
  localization: {
    locale: 'en-US',
    timeFormatter: fmtCrosshair,
    dateFormat: 'yyyy-MM-dd',
  },
  crosshair: {
    mode: CrosshairMode.Normal,
    vertLine: { color: '#00e5ff', width: 1, style: LineStyle.Dashed, labelBackgroundColor: '#00e5ff' },
    horzLine: { color: '#00e5ff', width: 1, style: LineStyle.Dashed, labelBackgroundColor: '#00e5ff' },
  },
  // Interaction — allow smooth pan on both axes; the price axis
  // becomes manually-draggable only when 2D pan is toggled ON via
  // setPan2D(). Default lightweight-charts only allows horizontal
  // time-scale drag, which feels janky when zoomed in.
  handleScroll: {
    mouseWheel: true,
    pressedMouseMove: true,
    horzTouchDrag: true,
    vertTouchDrag: true,
  },
  handleScale: {
    mouseWheel: true,
    pinch: true,
    axisPressedMouseMove: { time: true, price: false },
    axisDoubleClickReset: { time: true, price: true },
  },
  kineticScroll: {
    touch: true,
    mouse: true,
  },
};

const PRICE_UP = '#00ff88';
const PRICE_DN = '#ff3366';

function heikinAshi(candles) {
  const ha = [];
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    const prev = ha[i - 1];
    const close = (c.open + c.high + c.low + c.close) / 4;
    const open = prev ? (prev.open + prev.close) / 2 : (c.open + c.close) / 2;
    const high = Math.max(c.high, open, close);
    const low = Math.min(c.low, open, close);
    ha.push({ time: c.time, open, high, low, close });
  }
  return ha;
}

export class LightweightChartsProvider {
  constructor(container, opts = {}) {
    this.container = container;
    // Explicit width/height + a manual ResizeObserver — using
    // lightweight-charts' `autoSize:true` while also passing sizes
    // logs a warning and can fight with our own resize handler.
    this.chart = createChart(container, {
      width: container.clientWidth || 800,
      height: container.clientHeight || 500,
      ...THEME,
    });
    // Main price series — swapped when chart type changes.
    this.type = 'candles';
    this.candles = [];
    this._mountPriceSeries('candles');
    // Volume series lives in a separate price scale so it renders as a
    // bottom pane without stealing height from the price panel.
    this.volumeSeries = this.chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
      color: 'rgba(0,229,255,0.55)',
    });
    this.chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.75, bottom: 0.0 },
    });
    // Capability tracker for plugin-owned line series (indicators).
    this._extraSeries = new Set();
    // Observe container resize (mobile + panel toggles).
    this._resizeObs = new ResizeObserver(() => {
      if (!this.chart) return;
      this.chart.applyOptions({
        width: this.container.clientWidth,
        height: this.container.clientHeight,
      });
    });
    this._resizeObs.observe(container);

    // Point the pan-drag emulator at the actual DOM node.
    this._container = container;

    // Ctrl+scroll = don't let the chart intercept the wheel event; the
    // page can then scroll normally (matches TradingView behavior). We
    // stopImmediatePropagation *before* lightweight-charts sees it.
    this._wheelHandler = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.stopImmediatePropagation();
        // We deliberately do NOT preventDefault — the browser handles
        // page scroll (or pinch-zoom on macOS trackpads).
      }
    };
    container.addEventListener('wheel', this._wheelHandler, { capture: true, passive: true });

    // Force the right price scale into auto-scale mode whenever the
    // user pans or zooms the time axis. lightweight-charts silently
    // "locks" the price scale after certain gestures (wheel-on-axis,
    // drag) which makes EMAs / VWAP appear to drift off-screen. We
    // re-enable autoscale on every visible-range change — this is
    // exactly the behaviour TradingView ships by default.
    this._reautoScale = () => {
      if (!this.chart) return;
      // In 2D-pan mode the user *wants* to drag the price axis, so
      // don't fight their manual pan.
      // Refresh price-band snapshot even in 2D-pan so the EMA gate
      // stays accurate as the user scrolls.
      this._refreshPriceExtents();
      if (this._pan2D) return;
      try {
        this.chart.priceScale('right').applyOptions({ autoScale: true });
      } catch { /* noop */ }
    };
    this.chart.timeScale().subscribeVisibleTimeRangeChange(this._reautoScale);
  }

  _mountPriceSeries(type) {
    if (this.priceSeries) {
      this.chart.removeSeries(this.priceSeries);
      this.priceSeries = null;
    }
    if (type === 'line') {
      this.priceSeries = this.chart.addSeries(LineSeries, {
        color: '#00e5ff', lineWidth: 2,
        priceLineColor: '#00e5ff', priceLineStyle: LineStyle.Dashed,
      });
    } else if (type === 'area') {
      this.priceSeries = this.chart.addSeries(AreaSeries, {
        lineColor: '#00e5ff', topColor: 'rgba(0,229,255,0.35)',
        bottomColor: 'rgba(0,229,255,0.02)', lineWidth: 2,
      });
    } else {
      // candles + heikin_ashi both use candlestick series
      this.priceSeries = this.chart.addSeries(CandlestickSeries, {
        upColor: PRICE_UP, downColor: PRICE_DN,
        borderUpColor: PRICE_UP, borderDownColor: PRICE_DN,
        wickUpColor: PRICE_UP, wickDownColor: PRICE_DN,
      });
    }
  }

  setChartType(type) {
    this.type = type;
    this._mountPriceSeries(type === 'heikin_ashi' ? 'candles' : type);
    if (this.candles.length) this.setData(this.candles);
  }

  _shape(candles) {
    const src = this.type === 'heikin_ashi' ? heikinAshi(candles) : candles;
    if (this.type === 'line' || this.type === 'area') {
      return src.map((c) => ({ time: c.time, value: c.close }));
    }
    return src.map((c) => ({
      time: c.time, open: c.open, high: c.high, low: c.low, close: c.close,
    }));
  }

  setData(candles) {
    this.candles = candles || [];
    this.priceSeries.setData(this._shape(this.candles));
    // volume — colour by candle direction.
    this.volumeSeries.setData(
      this.candles.map((c) => ({
        time: c.time,
        value: c.volume ?? 0,
        color: (c.close >= c.open) ? 'rgba(0,255,136,0.45)' : 'rgba(255,51,102,0.45)',
      })),
    );
    // Snapshot the FULL price extent — used by the EMA
    // autoscaleInfoProvider (Phase 52.1 G2 #1) to decide whether an
    // EMA is close enough to price to contribute to the right-axis
    // auto-scale. The visible-range refinement happens in
    // `_refreshPriceExtents()` on every timeScale event.
    this._refreshPriceExtents();
    // fitContent() ONLY when the underlying series identity changes
    // (first load OR symbol/timeframe switch). Otherwise we'd wipe
    // out the user's pan/zoom every time new live data arrives.
    const firstTs = this.candles[0]?.time ?? null;
    const lastCount = this.candles.length;
    if (firstTs !== this._lastSeriesId || lastCount === 0 || this._lastCount === 0) {
      this._lastSeriesId = firstTs;
      this.chart.timeScale().fitContent();
    }
    this._lastCount = lastCount;
    // Re-enable autoscale on the right price axis whenever a full
    // dataset lands. Ensures EMAs / VWAP that were added *after* the
    // user last touched the axis still get included in the auto-fit.
    if (!this._pan2D) {
      try { this.chart.priceScale('right').applyOptions({ autoScale: true }); } catch { /* noop */ }
    }
  }

  /**
   * Refresh the visible-range price extents (min/max of candle wicks
   * within the current visible time window). Called on every data
   * update and every visibleTimeRangeChange. Used by the EMA
   * autoscaleInfoProvider so long-period EMAs that drift far from the
   * currently visible price don't over-expand the price axis.
   */
  _refreshPriceExtents() {
    if (!this.candles.length) { this._priceExtents = null; return; }
    let from = -Infinity, to = Infinity;
    try {
      const vr = this.chart.timeScale().getVisibleRange();
      if (vr && vr.from != null && vr.to != null) { from = vr.from; to = vr.to; }
    } catch { /* first-run — no range yet */ }
    let lo = Infinity, hi = -Infinity;
    for (const c of this.candles) {
      if (c.time < from || c.time > to) continue;
      if (c.low < lo) lo = c.low;
      if (c.high > hi) hi = c.high;
    }
    if (!isFinite(lo) || !isFinite(hi)) {
      // No candles in the visible window (initial mount) — fall back
      // to the full range so the gating still works.
      for (const c of this.candles) {
        if (c.low < lo) lo = c.low;
        if (c.high > hi) hi = c.high;
      }
    }
    this._priceExtents = { min: lo, max: hi };
  }

  updateLast(candle) {
    if (!candle) return;
    const last = this.candles[this.candles.length - 1];
    if (last && last.time === candle.time) this.candles[this.candles.length - 1] = candle;
    else this.candles.push(candle);
    const shaped = this._shape(this.candles);
    this.priceSeries.update(shaped[shaped.length - 1]);
    this.volumeSeries.update({
      time: candle.time, value: candle.volume ?? 0,
      color: (candle.close >= candle.open) ? 'rgba(0,255,136,0.45)' : 'rgba(255,51,102,0.45)',
    });
  }

  resize(w, h) {
    this.chart.applyOptions({ width: w, height: h });
  }

  /**
   * setPan2D(on) — allow the user to drag the right-price axis in
   * addition to the horizontal time axis. When ON: dragging the
   * price axis (right column of the chart) scrolls the price view;
   * double-clicking the axis snaps back to auto-fit.
   *
   * Note: lightweight-charts doesn't expose a programmatic
   * setVisiblePriceRange API, so we can't pan the price view from
   * inside the chart body. The 2D-pan toggle enables the axis-drag
   * gesture (which is the standard TradingView UX).
   */
  setPan2D(on) {
    this._pan2D = !!on;
    this.chart.applyOptions({
      handleScale: {
        axisPressedMouseMove: { time: true, price: !!on },
      },
    });
  }

  // ── Plugin capability surface ─────────────────────────────────────
  capability(name, opts = {}) {
    if (name === 'lineSeries') {
      // Phase 52.1 Group 2 #1 — TradingView-style EMA gating.
      //
      // When the plugin declares `constrainedToPriceBand:true` (used
      // by every EMA / VWAP / long-period overlay), we give the
      // series an autoscaleInfoProvider that returns null whenever
      // the series' natural visible range would extend more than
      // `bandPct` beyond the visible price extent. Practical effect:
      //   • EMA 20 hugging price ⇒ contributes to axis, price+EMA
      //     both fit comfortably.
      //   • EMA 500 drifted 30% below current price ⇒ excluded from
      //     axis, price stays comfortably framed, the far EMA is
      //     allowed to render off-screen (which is exactly what
      //     TradingView does).
      // The gate is decided per-frame; if the user zooms out until
      // the EMA is back within the band it snaps back into scale.
      const bandPct = opts.bandPct ?? 0.10;   // 10% by default
      const seriesOpts = {
        color: opts.color || '#00e5ff',
        lineWidth: opts.lineWidth || 1,
        lastValueVisible: opts.lastValueVisible ?? false,
        priceLineVisible: false,
        crosshairMarkerVisible: false,
        // Explicit shared price scale so EMA / VWAP contribute to the
        // right axis auto-scale and stay visually pinned to the price.
        priceScaleId: 'right',
      };
      if (opts.constrainedToPriceBand) {
        // Capture `this` — the arrow keeps it lexically bound.
        seriesOpts.autoscaleInfoProvider = (baseImpl) => {
          const info = baseImpl();
          const px = this._priceExtents;
          if (!info?.priceRange || !px) return info;
          const span = px.max - px.min;
          if (span <= 0) return info;
          const lo = px.min - span * bandPct;
          const hi = px.max + span * bandPct;
          const rMin = info.priceRange.minValue;
          const rMax = info.priceRange.maxValue;
          if (rMin < lo || rMax > hi) {
            // EMA too far from price — omit from axis scale.
            return null;
          }
          return info;
        };
      }
      const s = this.chart.addSeries(LineSeries, seriesOpts);
      this._extraSeries.add(s);
      // A freshly-mounted overlay series can trigger the axis to
      // "remember" a wider fixed range. Re-arm autoScale so the newly
      // added values (e.g. an EMA that's far from price) are pulled
      // into the visible window immediately.
      if (!this._pan2D) {
        try { this.chart.priceScale('right').applyOptions({ autoScale: true }); } catch { /* noop */ }
      }
      return {
        kind: 'lineSeries',
        setData: (rows) => {
          s.setData(rows);
          if (!this._pan2D) {
            try { this.chart.priceScale('right').applyOptions({ autoScale: true }); } catch { /* noop */ }
          }
        },
        update: (row) => s.update(row),
        release: () => {
          this._extraSeries.delete(s);
          try { this.chart.removeSeries(s); } catch { /* noop */ }
        },
      };
    }
    if (name === 'priceLine') {
      // Persistent horizontal price line on the main series.
      const line = this.priceSeries.createPriceLine({
        price: opts.price,
        color: opts.color || '#f7c948',
        lineStyle: LineStyle.Dashed,
        lineWidth: 1,
        axisLabelVisible: true,
        title: opts.title || '',
      });
      return {
        kind: 'priceLine',
        release: () => { try { this.priceSeries.removePriceLine(line); } catch { /* noop */ } },
      };
    }
    return null;
  }

  releaseCapability(handle) { handle?.release?.(); }

  /**
   * Right-axis width in CSS pixels — used by IndicatorPane to line
   * up its bars/lines and its own Y-axis label strip with the main
   * chart's price scale. Reads live so a symbol swap (e.g. tokens
   * with wildly different digit counts) reflows the panes correctly.
   */
  priceScaleWidth() {
    try { return this.chart.priceScale('right').width(); }
    catch { return 78; }   // safe fallback matching the old hard-code
  }

  /**
   * Convert a UNIX timestamp (seconds) to a pixel X coordinate on
   * the chart body. IndicatorPane uses this to place each histogram
   * bar directly under its parent candle so alignment is exact even
   * as the user zooms/pans (Phase 52.1 G2 #5).
   */
  timeToX(time) {
    try {
      const x = this.chart.timeScale().timeToCoordinate(time);
      return typeof x === 'number' ? x : null;
    } catch { return null; }
  }

  /**
   * Convert a price value to a pixel Y coordinate on the chart body.
   * Used by BandFillOverlay to shade the area between DBB/BB upper
   * and lower bands the way TradingView paints them.
   */
  priceToY(price) {
    try {
      const y = this.priceSeries?.priceToCoordinate(price);
      return typeof y === 'number' ? y : null;
    } catch { return null; }
  }

  snapshotDataUrl() {
    try { return this.chart.takeScreenshot().toDataURL('image/png'); }
    catch { return null; }
  }

  destroy() {
    try { this._resizeObs?.disconnect(); } catch { /* noop */ }
    if (this._wheelHandler && this._container) {
      try { this._container.removeEventListener('wheel', this._wheelHandler, { capture: true }); } catch { /* noop */ }
    }
    if (this._reautoScale && this.chart) {
      try { this.chart.timeScale().unsubscribeVisibleTimeRangeChange(this._reautoScale); } catch { /* noop */ }
    }
    for (const s of this._extraSeries) { try { this.chart.removeSeries(s); } catch { /* noop */ } }
    this._extraSeries.clear();
    try { this.chart.remove(); } catch { /* noop */ }
    this.chart = null;
  }
}

export default LightweightChartsProvider;
